import React, { useState } from "react";
import axios from 'axios';
import "./SendFeedback.css";
import CloudinaryUploadWidget from "../../../app/components/CloudinaryUploadWidget";
import { useTranslation } from 'react-i18next';
import { useToast } from "@chakra-ui/react";

export default function SendFeedback() {
  const [text, setText] = useState("");
  const [imageInfo, setImageInfo] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!text.trim()) {
      toast({
        title: t("chat.feedbackEmptyWarning"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    
    let feedbackText = text;
    
    if (imageInfo) {
      feedbackText += `\n![image feedback](${imageInfo.url})`;
    }

    try {
      await axios.post(
        '/api/v1/clients/chat/feedback/',
        {
          message: feedbackText,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      );
      
      toast({
        title: t("chat.feedbackSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      setText("");
      setImageInfo(null);
      
    } catch (e) {
      console.error("Error submitting feedback:", e);
      toast({
        title: t("chat.feedbackError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    
    setIsSubmitting(false);
  }

  return (
    <div className="feedback-container">
      <div className="feedback-wrapper">
        {/* Header Card */}
        <div className="feedback-card">
          <div className="feedback-content">
            <div className="feedback-header">
              <div className="feedback-icon">
                💬
              </div>
              <div>
                <h1 className="feedback-title">{t("chat.shareYourFeedback")}</h1>
                <p className="feedback-subtitle">{t("chat.feedbackSubtitle")}</p>
              </div>
            </div>

            <p className="feedback-description">
              {t("chat.feedbackDescription")}
            </p>

            <ul className="feedback-guidelines">
              <li className="feedback-guideline">
                <span className="feedback-guideline-label">{t("chat.feedbackGuidelineSpecific")}</span>
                <span>{t("chat.feedbackGuidelineSpecificDesc")}</span>
              </li>
              <li className="feedback-guideline">
                <span className="feedback-guideline-label">{t("chat.feedbackGuidelineConstructive")}</span>
                <span>{t("chat.feedbackGuidelineConstructiveDesc")}</span>
              </li>
              <li className="feedback-guideline">
                <span className="feedback-guideline-label">{t("chat.feedbackGuidelineRespectful")}</span>
                <span>{t("chat.feedbackGuidelineRespectfulDesc")}</span>
              </li>
            </ul>

            <form onSubmit={handleSubmit} className="feedback-form">
              <label htmlFor="feedback" className="feedback-label">
                {t("chat.feedbackYourMessage")}
              </label>
              <textarea
                id="feedback"
                required
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("chat.feedbackPlaceholder")}
                className="feedback-textarea"
                aria-describedby="feedback-help"
                disabled={isSubmitting}
              />
              <p id="feedback-help" className="feedback-help">
                {t("chat.feedbackHelpText")}
              </p>

              <div className="feedback-file-section" onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}>
                <CloudinaryUploadWidget
                  isImageOnly={true}
                  onImageUpload={(newImageInfo) => setImageInfo(newImageInfo)}
                  onDelete={() => {
                    setImageInfo(null);
                  }}
                />

                {imageInfo && (
                  <span className="feedback-file-count">{t("chat.feedbackImageSelected")}</span>
                )}
              </div>

              <div className="feedback-submit-section">
                <div className="feedback-buttons">
                  {isSubmitting ? (
                    <div>{t("chat.feedbackSending")}</div>
                  ) : (
                    <>
                      <button
                        type="submit"
                        className="feedback-submit-button"
                        aria-label={t("chat.sendFeedback")}
                        disabled={isSubmitting}
                      >
                        {t("chat.feedbackSendButton")}
                      </button>
                      <button
                        type="button"
                        className="feedback-submit-button clear"
                        onClick={() => {
                          setText("");
                          setImageInfo(null);
                        }}
                      >
                        {t("chat.feedbackClearButton")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        <p className="feedback-footer">
          {t("chat.feedbackFooter")}
        </p>
      </div>
    </div>
  );
}
